import { default as React, useCallback } from 'react';
import {
  makeStyles,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Paper,
} from '@material-ui/core';
import { getLogLevelColor } from '../../utils/chartUtils';

const useStyles = makeStyles(theme => ({
  dialogPaper: {
    minWidth: '80vw',
  },
  content: {
    padding: theme.spacing(2),
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    maxHeight: '70vh',
    overflow: 'auto',
    backgroundColor: theme.palette.background.default,
  },
  metadata: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  metadataItem: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
  },
  metadataLabel: {
    fontWeight: 'bold',
    marginRight: theme.spacing(1),
    minWidth: '100px',
  },
  jsonView: {
    fontFamily: 'monospace',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
  },
}));

export interface LogDialogEntry {
  timestamp: string;
  level: string;
  message: string;
  labels?: Record<string, string>;
  count?: number;
  serviceName?: string;
}

interface LogDialogProps {
  open: boolean;
  onClose: () => void;
  log: LogDialogEntry | null;
}

const LogDialog = React.memo(({ open, onClose, log }: LogDialogProps) => {
  const classes = useStyles();

  const formatMessage = useCallback((message: string) => {
    try {
      if (message.trim().startsWith('{') && message.trim().endsWith('}')) {
        const jsonObj = JSON.parse(message);
        return (
          <pre className={classes.jsonView}>
            {JSON.stringify(jsonObj, null, 2)}
          </pre>
        );
      }
      return message;
    } catch {
      return message;
    }
  }, [classes.jsonView]);

  if (!log) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      classes={{ paper: classes.dialogPaper }}
    >
      <DialogTitle>
        Detalhes do Log
        <Chip
          label={log.level}
          size="small"
          style={{
            backgroundColor: getLogLevelColor(log.level),
            color: '#fff',
            marginLeft: '8px',
            marginRight: '8px',
          }}
        />
        {log.serviceName && (
          <Chip
            label={log.serviceName}
            size="small"
            color="primary"
          />
        )}
      </DialogTitle>
      <DialogContent>
        <div className={classes.metadata}>
          <div className={classes.metadataItem}>
            <span className={classes.metadataLabel}>Timestamp:</span>
            <span>{log.timestamp}</span>
          </div>
          {log.count !== undefined && (
            <div className={classes.metadataItem}>
              <span className={classes.metadataLabel}>Ocorrencias:</span>
              <span>{log.count}</span>
            </div>
          )}
          {log.labels && (
            <div className={classes.metadataItem}>
              <span className={classes.metadataLabel}>Labels:</span>
              <span>
                {Object.entries(log.labels).map(([key, value]) => (
                  <Chip
                    key={key}
                    label={`${key}: ${value}`}
                    size="small"
                    style={{ margin: '2px' }}
                  />
                ))}
              </span>
            </div>
          )}
        </div>
        <Paper variant="outlined" className={classes.content}>
          {formatMessage(log.message)}
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
});

export default LogDialog;
